import React, { useEffect, useState } from 'react';
import { supabase, db } from '../services/db';
import { PageHeader } from './ui/PageHeader';
import { Database, Loader2, Search, ShieldAlert, CheckCircle, XCircle } from 'lucide-react';
import { PLAN_DEFINITIONS, PlanKey } from '../utils/plans';
import { ConfirmModal } from './ui/ConfirmModal';

interface SuperadminDashboardProps {
  currentUser: any;
  onImpersonatingChange?: (data: { empresaId: string; empresaNome: string } | null) => void;
}

// ── Helpers ──────────────────────────────────────────────────────────────────
const getAvatarColor = (name: string): string => {
  const colors = ['#3b82f6','#8b5cf6','#ec4899','#f59e0b','#10b981','#ef4444','#06b6d4','#f97316'];
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return colors[Math.abs(hash) % colors.length];
};

const getInitials = (name: string): string =>
  name.split(' ').slice(0, 2).map(w => w[0]).join('').toUpperCase();

const formatLastAccess = (iso: string): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  const diff = Math.floor((Date.now() - d.getTime()) / 60000);
  if (diff < 60)   return `${diff}min atrás`;
  if (diff < 1440) return `hoje, ${d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;
  if (diff < 2880) return `ontem, ${d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }) + ', ' +
         d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
};

const planBadge = (plano: string) => {
  const map: Record<string, [string, string]> = {
    business: ['rgba(251,146,60,.18)', '#fb923c'],
    pro:      ['rgba(168,85,247,.18)', '#c084fc'],
    trial:    ['rgba(96,165,250,.18)', '#60a5fa'],
    starter:  ['rgba(148,163,184,.12)', '#94a3b8'],
  };
  const [bg, color] = map[plano] ?? map.starter;
  return (
    <span style={{ background: bg, color, border: `1px solid ${color}44`, borderRadius: 6,
      padding: '2px 9px', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em' }}>
      {plano}
    </span>
  );
};

const statusBadge = (status: string) => {
  const map: Record<string, [string, string]> = {
    active:    ['rgba(16,185,129,.15)', '#34d399'],
    trial:     ['rgba(59,130,246,.15)', '#60a5fa'],
    expired:   ['rgba(239,68,68,.15)', '#f87171'],
    suspended: ['rgba(245,158,11,.15)', '#fb923c'],
  };
  const [bg, color] = map[status] ?? ['rgba(148,163,184,.12)', '#94a3b8'];
  return (
    <span style={{ background: bg, color, border: `1px solid ${color}44`, borderRadius: 6,
      padding: '2px 9px', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em' }}>
      {status}
    </span>
  );
};

// ── Component ─────────────────────────────────────────────────────────────────
export const SuperadminDashboard: React.FC<SuperadminDashboardProps> = ({ currentUser, onImpersonatingChange }) => {
  const [empresas,     setEmpresas]     = useState<any[]>([]);
  const [counts,       setCounts]       = useState<any[]>([]);
  const [ordersCount,  setOrdersCount]  = useState<Record<string, number>>({});
  const [lastAccess,   setLastAccess]   = useState<Record<string, string>>({});
  const [isLoading,    setIsLoading]    = useState(true);
  const [search,       setSearch]       = useState('');
  const [filterPlano,  setFilterPlano]  = useState('todos');
  const [filterStatus, setFilterStatus] = useState('todos');

  // impersonar
  const [impersonating,        setImpersonating]        = useState<{ empresaId: string; empresaNome: string } | null>(null);
  const [showImpersonateModal, setShowImpersonateModal] = useState(false);
  const [selectedEmpresa,      setSelectedEmpresa]      = useState<any>(null);

  // modal de confirmação/alerta elegante
  const [confirmModal, setConfirmModal] = useState<{
    title: string;
    message: string;
    confirmLabel?: string;
    cancelLabel?: string;
    confirmStyle?: 'danger' | 'primary';
    onConfirm: () => void;
    onCancel?: () => void;
  } | null>(null);

  // Ativação manual
  const [activateModal, setActivateModal] = useState<{empresaId: string; nome: string} | null>(null);
  const [activatePlano, setActivatePlano] = useState('pro');
  const [activateNotes, setActivateNotes] = useState('');

  // exclusão de empresa
  const [deleteModal, setDeleteModal] = useState<{
    empresaId: string;
    nomeEmpresa: string;
    nomeAdmin: string;
  } | null>(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);

  // ── Carga ───────────────────────────────────────────────────────────────
  const loadData = async () => {
    setIsLoading(true);
    try {
      const [empRes, usersRes, ordersRes, lastRes] = await Promise.all([
        supabase.from('empresas')
          .select('id,nome,email,plano,plano_status,plano_expira_em,status,criado_em,trial_start,trial_end,activated_at,activated_by,payment_notes')
          .order('criado_em', { ascending: false }),
        supabase.from('users').select('empresa_id,role'),
        supabase.from('orders').select('empresa_id,status'),
        supabase.from('users').select('empresa_id,updated_at').order('updated_at', { ascending: false }),
      ]);

      if (empRes.error)   throw empRes.error;
      if (usersRes.error) throw usersRes.error;

      // contagens por empresa
      const countMap: Record<string, any> = {};
      (usersRes.data || []).forEach((u: any) => {
        if (!countMap[u.empresa_id]) countMap[u.empresa_id] = {
          empresa_id: u.empresa_id, total_tecnicos: 0, total_admins: 0, total_assistentes: 0, total_clientes: 0,
        };
        if (u.role === 'TECHNICIAN') countMap[u.empresa_id].total_tecnicos++;
        if (u.role === 'ADMIN')      countMap[u.empresa_id].total_admins++;
        if (u.role === 'ASSISTANT')  countMap[u.empresa_id].total_assistentes++;
        if (u.role === 'CLIENT')     countMap[u.empresa_id].total_clientes++;
      });

      // OS abertas por empresa
      const oMap: Record<string, number> = {};
      (ordersRes.data || []).forEach((o: any) => {
        if (o.status !== 'COMPLETED' && o.status !== 'CANCELLED')
          oMap[o.empresa_id] = (oMap[o.empresa_id] || 0) + 1;
      });

      // último acesso por empresa
      const laMap: Record<string, string> = {};
      (lastRes.data || []).forEach((u: any) => {
        if (!laMap[u.empresa_id]) laMap[u.empresa_id] = u.updated_at;
      });

      setEmpresas(empRes.data || []);
      setCounts(Object.values(countMap));
      setOrdersCount(oMap);
      setLastAccess(laMap);
    } catch (err: any) {
      console.error('Erro superadmin:', err?.message || err);
      setConfirmModal({
        title: 'Erro de Carregamento',
        message: `Erro ao carregar: ${err?.message || 'desconhecido'}`,
        onConfirm: () => setConfirmModal(null),
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  // Notifica o pai sempre que o estado de impersonação mudar
  useEffect(() => { onImpersonatingChange?.(impersonating); }, [impersonating]);

  // ── Handlers trial / ativacao ──────────────────────────────────────────────
  const handleExtendTrial = async (empresaId: string, days: number) => {
    try {
      await db.extendTrial(empresaId, days);
      setConfirmModal({
        title: 'Sucesso',
        message: `Trial estendido por +${days} dias!`,
        onConfirm: () => setConfirmModal(null)
      });
      loadData();
    } catch (e: any) {
      setConfirmModal({
        title: 'Erro',
        message: `Erro ao estender trial: ${e.message || e}`,
        onConfirm: () => setConfirmModal(null)
      });
    }
  };

  const handleSuspendEmpresa = async (empresaId: string) => {
    const reason = prompt('Motivo da suspensão (opcional):');
    if (reason === null) return;
    try {
      await db.suspendEmpresa(empresaId, reason);
      setConfirmModal({
        title: 'Sucesso',
        message: 'Empresa suspensa com sucesso!',
        onConfirm: () => setConfirmModal(null)
      });
      loadData();
    } catch (e: any) {
      setConfirmModal({
        title: 'Erro',
        message: `Erro ao suspender empresa: ${e.message || e}`,
        onConfirm: () => setConfirmModal(null)
      });
    }
  };

  const handleChangePlan = async (empresaId: string, plano: string) => {
    try {
      await db.activateEmpresa(empresaId, plano, 'Plano alterado pelo SUPERADMIN');
      setConfirmModal({
        title: 'Sucesso',
        message: `Plano alterado para ${plano.toUpperCase()} com sucesso!`,
        onConfirm: () => setConfirmModal(null)
      });
      loadData();
    } catch (e: any) {
      setConfirmModal({
        title: 'Erro',
        message: `Erro ao alterar plano: ${e.message || e}`,
        onConfirm: () => setConfirmModal(null)
      });
    }
  };

  const handleConfirmActivate = async () => {
    if (!activateModal) return;
    try {
      await db.activateEmpresa(activateModal.empresaId, activatePlano, activateNotes);
      setConfirmModal({
        title: 'Sucesso',
        message: `Empresa "${activateModal.nome}" ativada no plano ${activatePlano.toUpperCase()}!`,
        onConfirm: () => setConfirmModal(null)
      });
      setActivateModal(null);
      setActivatePlano('pro');
      setActivateNotes('');
      loadData();
    } catch (e: any) {
      setConfirmModal({
        title: 'Erro',
        message: `Erro ao ativar empresa: ${e.message || e}`,
        onConfirm: () => setConfirmModal(null)
      });
    }
  };

  // ── Handlers impersonar ─────────────────────────────────────────────────
  const handleConfirmImpersonate = async () => {
    if (!selectedEmpresa) return;
    try {
      await supabase.from('audit_logs').insert({
        user_id: currentUser.id,
        action: `SUPERADMIN_IMPERSONATE_START: ${selectedEmpresa.nome}`,
        details: { empresaId: selectedEmpresa.id, empresaNome: selectedEmpresa.nome },
        timestamp: new Date().toISOString(),
        empresa_id: selectedEmpresa.id,
      });
    } catch (_) {}
    setImpersonating({ empresaId: selectedEmpresa.id, empresaNome: selectedEmpresa.nome });
    setShowImpersonateModal(false);
    setSelectedEmpresa(null);
  };

  const handleExitImpersonate = async () => {
    if (!impersonating) return;
    try {
      await supabase.from('audit_logs').insert({
        user_id: currentUser.id,
        action: `SUPERADMIN_IMPERSONATE_END: ${impersonating.empresaNome}`,
        details: { empresaId: impersonating.empresaId },
        timestamp: new Date().toISOString(),
        empresa_id: impersonating.empresaId,
      });
    } catch (_) {}
    setImpersonating(null);
  };

  // ── Guard ────────────────────────────────────────────────────────────────
  if (currentUser?.role !== 'SUPERADMIN') {
    return (
      <div className="flex flex-col items-center justify-center h-full pt-20">
        <ShieldAlert size={64} className="text-red-500 mb-4" />
        <h2 className="text-2xl font-bold">Acesso Negado</h2>
        <p className="text-gray-500">Apenas superadministradores podem ver esta página.</p>
      </div>
    );
  }

  // ── Métricas ─────────────────────────────────────────────────────────────
  const totalEmpresas = empresas.length;
  const ativas        = empresas.filter(e => e.status === 'active').length;
  const emTrial       = empresas.filter(e => e.status === 'trial').length;
  const suspensas     = empresas.filter(e => e.status === 'suspended' || e.status === 'expired').length;

  const metricCards = [
    { label: 'TOTAL DE EMPRESAS', value: totalEmpresas, sublabel: 'cadastradas',   color: 'var(--sf-white)', border: 'rgba(255,255,255,.2)' },
    { label: 'ATIVAS',            value: ativas,        sublabel: 'últimos 7 dias', color: '#10b981',         border: '#10b981' },
    { label: 'EM TRIAL',          value: emTrial,       sublabel: '14 dias grátis', color: '#f59e0b',         border: '#f59e0b' },
    { label: 'SUSPENSAS',         value: suspensas,     sublabel: 'inadimplência',  color: '#ef4444',         border: '#ef4444' },
  ];

  const filtered = empresas
    .filter(e => filterPlano  === 'todos' || e.plano        === filterPlano)
    .filter(e => filterStatus === 'todos' || e.plano_status === filterStatus)
    .filter(e =>
      e.nome?.toLowerCase().includes(search.toLowerCase()) ||
      e.email?.toLowerCase().includes(search.toLowerCase())
    );

  const TH_STYLE: React.CSSProperties = {
    padding: '12px 16px', fontSize: 10, fontWeight: 700,
    color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.06em',
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      <div className="space-y-6">
        <PageHeader
          title="Superadmin | Instâncias"
          description="Gestão global de empresas e assinaturas do ServiceFlow."
          icon={<Database className="text-red-600" size={24} />}
        />

        {/* Cards de métricas */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 16 }}>
          {metricCards.map(card => (
            <div key={card.label} style={{
              background: 'rgba(255,255,255,.03)', border: '1px solid rgba(255,255,255,.07)',
              borderLeft: `3px solid ${card.border}`, borderRadius: 12, padding: '16px 20px',
            }}>
              <p style={{ fontSize: 10, fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.06em' }}>
                {card.label}
              </p>
              <p style={{ fontSize: 32, fontWeight: 700, color: card.color, margin: '6px 0 4px' }}>
                {card.value}
              </p>
              <p style={{ fontSize: 12, color: 'var(--muted)' }}>{card.sublabel}</p>
            </div>
          ))}
        </div>

        {/* Busca + Filtros */}
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <div style={{
            flex: 1, background: 'rgba(255,255,255,.04)', border: '1px solid rgba(255,255,255,.1)',
            borderRadius: 12, padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 10,
          }}>
            <Search size={16} style={{ color: 'var(--muted)', flexShrink: 0 }} />
            <input
              type="text" placeholder="Buscar empresa por nome ou email..."
              value={search} onChange={e => setSearch(e.target.value)}
              style={{ flex: 1, background: 'transparent', border: 'none', outline: 'none',
                color: 'var(--sf-white)', fontSize: 14 }}
            />
          </div>
          <select
            value={filterPlano} onChange={e => setFilterPlano(e.target.value)}
            style={{
              background: 'rgba(255,255,255,.05)', border: '1px solid rgba(255,255,255,.12)',
              borderRadius: 10, padding: '10px 14px', color: 'var(--sf-white)',
              fontSize: 13, height: 44, minWidth: 160, cursor: 'pointer', outline: 'none',
            }}>
            <option value="todos">Todos os planos</option>
            <option value="starter">Starter</option>
            <option value="pro">Pro</option>
            <option value="business">Business</option>
            <option value="trial">Trial</option>
          </select>
          <select
            value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
            style={{
              background: 'rgba(255,255,255,.05)', border: '1px solid rgba(255,255,255,.12)',
              borderRadius: 10, padding: '10px 14px', color: 'var(--sf-white)',
              fontSize: 13, height: 44, minWidth: 160, cursor: 'pointer', outline: 'none',
            }}>
            <option value="todos">Todos os status</option>
            <option value="ativo">Ativo</option>
            <option value="inativo">Inativo</option>
            <option value="cancelado">Cancelado</option>
          </select>
        </div>

        {/* Tabela */}
        {isLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
            <Loader2 size={40} style={{ color: 'var(--blue)' }} className="animate-spin" />
          </div>
        ) : (
          <div style={{ background: 'rgba(255,255,255,.03)', border: '1px solid rgba(255,255,255,.08)',
            borderRadius: 16, overflow: 'hidden' }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid rgba(255,255,255,.08)' }}>
                    {['Empresa','Plano','Status','Técnicos','OS Abertas','Último Acesso','Ações'].map(h => (
                      <th key={h} style={TH_STYLE}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(empresa => {
                    const uso   = counts.find(c => c.empresa_id === empresa.id);
                    const cor   = getAvatarColor(empresa.nome || '');
                    const osQty = ordersCount[empresa.id] ?? 0;
                    const la    = lastAccess[empresa.id];

                    return (
                      <tr key={empresa.id}
                        style={{ borderBottom: '1px solid rgba(255,255,255,.05)', transition: 'background .15s' }}
                        onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,255,255,.03)')}
                        onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                      >
                        {/* Empresa */}
                        <td style={{ padding: '14px 16px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            <div style={{
                              width: 40, height: 40, borderRadius: 10, flexShrink: 0,
                              background: cor, display: 'flex', alignItems: 'center',
                              justifyContent: 'center', fontSize: 13, fontWeight: 700, color: '#fff',
                            }}>
                              {getInitials(empresa.nome || '?')}
                            </div>
                            <div>
                              <p style={{ fontWeight: 700, color: 'var(--sf-white)' }}>{empresa.nome}</p>
                              <p style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>{empresa.email}</p>
                              {empresa.payment_notes && (
                                <p style={{ fontSize: 11, color: 'var(--amber)', marginTop: 2, fontStyle: 'italic', maxWidth: 280, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                  Nota: {empresa.payment_notes}
                                </p>
                              )}
                              <p style={{ fontSize: 10, color: 'var(--hint)', marginTop: 2, fontFamily: 'monospace' }}>
                                ID: {empresa.id?.split('-')[0]}...
                              </p>
                            </div>
                          </div>
                        </td>

                        {/* Plano */}
                        <td style={{ padding: '14px 16px' }}>
                          {planBadge(empresa.plano)}
                          {empresa.status === 'trial' && empresa.trial_end && (
                            <>
                              <p style={{ fontSize: 10, color: 'var(--hint)', marginTop: 5 }}>
                                Trial expira: {new Date(empresa.trial_end).toLocaleDateString('pt-BR')}
                              </p>
                              <p style={{ fontSize: 10, fontWeight: 600, color: 'var(--amber)', marginTop: 2 }}>
                                {Math.max(0, Math.ceil((new Date(empresa.trial_end).getTime() - Date.now()) / (1000 * 60 * 60 * 24)))} dias restantes
                              </p>
                            </>
                          )}
                          {empresa.status === 'active' && empresa.activated_at && (
                            <p style={{ fontSize: 10, color: 'var(--hint)', marginTop: 5 }}>
                              Ativado em: {new Date(empresa.activated_at).toLocaleDateString('pt-BR')}
                            </p>
                          )}
                        </td>

                        {/* Status */}
                        <td style={{ padding: '14px 16px' }}>
                          {statusBadge(empresa.status || 'trial')}
                        </td>

                        {/* Técnicos */}
                        <td style={{ padding: '14px 16px' }}>
                          <span style={{ fontSize: 18, fontWeight: 800,
                            color: uso?.total_tecnicos > 0 ? '#60a5fa' : 'var(--muted)' }}>
                            {uso ? uso.total_tecnicos : '—'}
                          </span>
                        </td>

                        {/* OS Abertas */}
                        <td style={{ padding: '14px 16px' }}>
                          <span style={{ fontSize: 18, fontWeight: 800,
                            color: osQty > 0 ? '#fb923c' : 'var(--muted)' }}>
                            {osQty}
                          </span>
                        </td>

                        {/* Último Acesso */}
                        <td style={{ padding: '14px 16px', fontSize: 12, color: 'var(--muted)' }}>
                          {formatLastAccess(la)}
                        </td>

                        {/* Ações */}
                        <td style={{ padding: '14px 16px' }}>
                          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                            {(empresa.status === 'trial' || empresa.status === 'expired') && (
                              <>
                                <button
                                  onClick={() => setActivateModal({ empresaId: empresa.id, nome: empresa.nome })}
                                  style={{ padding: '6px 10px', borderRadius: 8, fontSize: 11, fontWeight: 600,
                                    cursor: 'pointer', background: 'rgba(52,211,153,.15)',
                                    border: '1px solid rgba(52,211,153,.3)', color: '#34d399' }}>
                                  ✓ Ativar acesso
                                </button>
                                <button
                                  onClick={() => handleExtendTrial(empresa.id, 7)}
                                  style={{ padding: '6px 10px', borderRadius: 8, fontSize: 11, fontWeight: 600,
                                    cursor: 'pointer', background: 'rgba(96,165,250,.15)',
                                    border: '1px solid rgba(96,165,250,.3)', color: '#60a5fa' }}>
                                  +7 dias de trial
                                </button>
                              </>
                            )}

                            {empresa.status === 'active' && (
                              <>
                                <button
                                  onClick={() => handleSuspendEmpresa(empresa.id)}
                                  style={{ padding: '6px 10px', borderRadius: 8, fontSize: 11, fontWeight: 600,
                                    cursor: 'pointer', background: 'rgba(248,113,113,.15)',
                                    border: '1px solid rgba(248,113,113,.3)', color: '#f87171' }}>
                                  Suspender
                                </button>
                                <select
                                  value={empresa.plano}
                                  onChange={e => handleChangePlan(empresa.id, e.target.value)}
                                  style={{
                                    background: 'rgba(255,255,255,.05)', border: '1px solid rgba(255,255,255,.12)',
                                    borderRadius: 8, padding: '4px 8px', color: 'var(--sf-white)',
                                    fontSize: 11, height: 28, cursor: 'pointer', outline: 'none',
                                  }}
                                >
                                  <option value="starter">Starter</option>
                                  <option value="pro">Pro</option>
                                  <option value="business">Business</option>
                                </select>
                              </>
                            )}

                            {empresa.status === 'suspended' && (
                              <button
                                onClick={() => setActivateModal({ empresaId: empresa.id, nome: empresa.nome })}
                                style={{ padding: '6px 10px', borderRadius: 8, fontSize: 11, fontWeight: 600,
                                  cursor: 'pointer', background: 'rgba(52,211,153,.15)',
                                  border: '1px solid rgba(52,211,153,.3)', color: '#34d399' }}>
                                ✓ Reativar acesso
                              </button>
                            )}

                            <button
                              onClick={() => { setSelectedEmpresa(empresa); setShowImpersonateModal(true); }}
                              style={{ display: 'flex', alignItems: 'center', gap: 6,
                                padding: '6px 12px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                                background: 'rgba(162,105,247,.15)', border: '1px solid rgba(162,105,247,.3)', color: '#a269f7' }}>
                              👁 Entrar
                            </button>
                            <button
                              onClick={() => setDeleteModal({
                                empresaId: empresa.id,
                                nomeEmpresa: empresa.nome,
                                nomeAdmin: empresa.adminEmail || empresa.email || empresa.nome,
                              })}
                              style={{
                                background: 'var(--red-dim)',
                                border: '1px solid var(--red-border)',
                                borderRadius: 7, color: 'var(--red)',
                                fontSize: 12, padding: '5px 10px',
                                cursor: 'pointer',
                                display: 'flex', alignItems: 'center', gap: 5,
                              }}
                            >
                              <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                <polyline points="3 6 5 6 21 6"/>
                                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
                              </svg>
                              Excluir
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {filtered.length === 0 && (
                    <tr>
                      <td colSpan={7} style={{ padding: 40, textAlign: 'center', color: 'var(--muted)', fontSize: 13 }}>
                        Nenhuma empresa encontrada.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Modal impersonar */}
      {showImpersonateModal && selectedEmpresa && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.75)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000, padding: 16 }}>
          <div className="animate-fade-in" style={{ background: 'var(--navy3)',
            border: '1px solid rgba(255,255,255,.1)', borderRadius: 20,
            width: '100%', maxWidth: 480, boxShadow: '0 24px 80px rgba(0,0,0,.7)', overflow: 'hidden' }}>

            {/* Header modal */}
            <div style={{ padding: '18px 24px', borderBottom: '1px solid rgba(255,255,255,.08)',
              display: 'flex', alignItems: 'center', gap: 14, background: 'var(--navy2)' }}>
              <div style={{ width: 48, height: 48, borderRadius: 12, flexShrink: 0,
                background: getAvatarColor(selectedEmpresa.nome || ''),
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 18, fontWeight: 800, color: '#fff' }}>
                {getInitials(selectedEmpresa.nome || '?')}
              </div>
              <div>
                <p style={{ fontWeight: 700, color: 'var(--sf-white)', fontSize: 16 }}>{selectedEmpresa.nome}</p>
                <p style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2 }}>
                  Plano: <span style={{ textTransform: 'uppercase', fontWeight: 700 }}>{selectedEmpresa.plano}</span>
                  {' '}· {selectedEmpresa.email}
                </p>
              </div>
            </div>

            {/* Body modal */}
            <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>
              <p style={{ fontSize: 14, color: 'var(--sf-white)', lineHeight: 1.6 }}>
                Você está prestes a <strong>entrar no contexto desta empresa</strong> em modo de visualização.
              </p>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '12px 14px',
                borderRadius: 10, background: 'rgba(245,158,11,.1)', border: '1px solid rgba(245,158,11,.3)' }}>
                <span style={{ fontSize: 16, flexShrink: 0, marginTop: 1 }}>⚠️</span>
                <p style={{ fontSize: 12, color: '#fcd34d', lineHeight: 1.6 }}>
                  Todas as ações serão registradas no <strong>audit log</strong> com sua identificação de SUPERADMIN.
                </p>
              </div>
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 4 }}>
                <button
                  onClick={() => { setShowImpersonateModal(false); setSelectedEmpresa(null); }}
                  style={{ padding: '9px 20px', borderRadius: 10, fontSize: 13, fontWeight: 600,
                    background: 'rgba(255,255,255,.06)', border: '1px solid rgba(255,255,255,.1)',
                    color: 'var(--muted)', cursor: 'pointer' }}>
                  Cancelar
                </button>
                <button onClick={handleConfirmImpersonate}
                  style={{ padding: '9px 20px', borderRadius: 10, fontSize: 13, fontWeight: 600,
                    background: 'var(--blue)', border: 'none', color: '#fff', cursor: 'pointer' }}>
                  👁 Confirmar e entrar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Modal de Ativação Manual */}
      {activateModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.75)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000, padding: 16 }}>
          <div className="animate-fade-in" style={{ background: 'var(--navy3)',
            border: '1px solid rgba(255,255,255,.1)', borderRadius: 20,
            width: '100%', maxWidth: 480, boxShadow: '0 24px 80px rgba(0,0,0,.7)', overflow: 'hidden' }}>

            {/* Header modal */}
            <div style={{ padding: '18px 24px', borderBottom: '1px solid rgba(255,255,255,.08)',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'var(--navy2)' }}>
              <div>
                <p style={{ fontWeight: 700, color: 'var(--sf-white)', fontSize: 16 }}>Ativar Acesso Manual</p>
                <p style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2 }}>Empresa: {activateModal.nome}</p>
              </div>
              <button onClick={() => setActivateModal(null)} style={{ background: 'none', border: 'none', color: 'var(--muted)', cursor: 'pointer', fontSize: 18 }}>✕</button>
            </div>

            {/* Body modal */}
            <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div>
                <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 6, display: 'block' }}>
                  Plano a Ativar
                </label>
                <select
                  value={activatePlano}
                  onChange={e => setActivatePlano(e.target.value)}
                  style={{
                    width: '100%', background: 'rgba(255,255,255,.05)', border: '1px solid rgba(255,255,255,.12)',
                    borderRadius: 10, padding: '10px 14px', color: 'var(--sf-white)',
                    fontSize: 13, cursor: 'pointer', outline: 'none',
                  }}
                >
                  <option value="starter">Starter (R$ 149/mês)</option>
                  <option value="pro">Pro (R$ 299/mês)</option>
                  <option value="business">Business (R$ 399/mês)</option>
                </select>
              </div>

              <div>
                <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 6, display: 'block' }}>
                  Notas de Pagamento / Observações
                </label>
                <textarea
                  placeholder='Ex: PIX R$299 recebido em 27/05/2026 correspondente ao primeiro mês'
                  value={activateNotes}
                  onChange={e => setActivateNotes(e.target.value)}
                  rows={3}
                  style={{
                    width: '100%', background: 'rgba(255,255,255,.05)', border: '1px solid rgba(255,255,255,.12)',
                    borderRadius: 10, padding: '10px 14px', color: 'var(--sf-white)',
                    fontSize: 13, outline: 'none', resize: 'vertical',
                  }}
                />
              </div>

              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 4 }}>
                <button
                  onClick={() => setActivateModal(null)}
                  style={{ padding: '9px 20px', borderRadius: 10, fontSize: 13, fontWeight: 600,
                    background: 'rgba(255,255,255,.06)', border: '1px solid rgba(255,255,255,.1)',
                    color: 'var(--muted)', cursor: 'pointer' }}>
                  Cancelar
                </button>
                <button onClick={handleConfirmActivate}
                  style={{ padding: '9px 20px', borderRadius: 10, fontSize: 13, fontWeight: 600,
                    background: 'var(--blue)', border: 'none', color: '#fff', cursor: 'pointer' }}>
                  Confirmar e Ativar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {deleteModal && (
        <div
          style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.7)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:300, padding:20 }}
          onClick={() => { setDeleteModal(null); setDeleteConfirmText(''); }}
        >
          <div
            style={{ background:'var(--bg2)', border:'1px solid var(--red-border)', borderRadius:12, width:'100%', maxWidth:440, overflow:'hidden', boxShadow:'0 20px 60px rgba(0,0,0,.6)' }}
            onClick={e => e.stopPropagation()}
          >
            {/* Cabeçalho */}
            <div style={{ padding:'16px 20px', borderBottom:'1px solid var(--border)', display:'flex', alignItems:'center', gap:10 }}>
              <div style={{ width:36, height:36, borderRadius:8, background:'var(--red-dim)', border:'1px solid var(--red-border)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                <svg width="16" height="16" fill="none" stroke="var(--red)" strokeWidth="2" viewBox="0 0 24 24">
                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                  <line x1="12" y1="9" x2="12" y2="13"/>
                  <line x1="12" y1="17" x2="12.01" y2="17"/>
                </svg>
              </div>
              <div>
                <div style={{ fontSize:15, fontWeight:600, color:'var(--text)' }}>Excluir empresa</div>
                <div style={{ fontSize:12, color:'var(--muted)', marginTop:1 }}>Esta ação não pode ser desfeita</div>
              </div>
              <button
                onClick={() => { setDeleteModal(null); setDeleteConfirmText(''); }}
                style={{ marginLeft:'auto', background:'transparent', border:'none', color:'var(--muted)', cursor:'pointer', fontSize:18, lineHeight:1 }}
              >✕</button>
            </div>

            {/* Corpo */}
            <div style={{ padding:'20px' }}>
              <div style={{ background:'var(--red-dim)', border:'1px solid var(--red-border)', borderRadius:8, padding:'12px 14px', marginBottom:16 }}>
                <p style={{ fontSize:13, color:'var(--red)', fontWeight:600, marginBottom:4 }}>
                  ⚠ Atenção: esta ação é irreversível
                </p>
                <p style={{ fontSize:12, color:'var(--muted)', lineHeight:1.6 }}>
                  Todos os dados da empresa <strong style={{ color:'var(--text)' }}>{deleteModal.nomeEmpresa}</strong> serão
                  permanentemente excluídos, incluindo O.S., usuários, histórico e configurações.
                </p>
              </div>

              <p style={{ fontSize:13, color:'var(--muted)', marginBottom:8, lineHeight:1.6 }}>
                Para confirmar, digite o nome da empresa abaixo:
              </p>
              <p style={{ fontSize:13, fontWeight:600, color:'var(--text)', marginBottom:10, fontFamily:'DM Mono,monospace' }}>
                {deleteModal.nomeEmpresa}
              </p>
              <input
                type="text"
                value={deleteConfirmText}
                onChange={e => setDeleteConfirmText(e.target.value)}
                placeholder="Digite o nome da empresa para confirmar"
                style={{
                  width:'100%', background:'var(--bg3)',
                  border:`1px solid ${deleteConfirmText === deleteModal.nomeEmpresa ? 'var(--green-border)' : 'var(--border)'}`,
                  borderRadius:7, color:'var(--text)',
                  fontFamily:'Inter,sans-serif', fontSize:13,
                  padding:'9px 12px', outline:'none',
                  transition:'border-color .15s',
                }}
              />
              {deleteConfirmText.length > 0 && deleteConfirmText !== deleteModal.nomeEmpresa && (
                <p style={{ fontSize:11, color:'var(--red)', marginTop:5 }}>
                  Nome incorreto. Digite exatamente: {deleteModal.nomeEmpresa}
                </p>
              )}
            </div>

            {/* Rodapé */}
            <div style={{ padding:'14px 20px', borderTop:'1px solid var(--border)', display:'flex', justifyContent:'flex-end', gap:8 }}>
              <button
                onClick={() => { setDeleteModal(null); setDeleteConfirmText(''); }}
                style={{ padding:'8px 16px', borderRadius:7, fontSize:13, fontWeight:500, cursor:'pointer', background:'transparent', border:'1px solid var(--border)', color:'var(--muted)' }}
              >
                Cancelar
              </button>
              <button
                disabled={deleteConfirmText !== deleteModal.nomeEmpresa || deleting}
                onClick={async () => {
                  if (deleteConfirmText !== deleteModal.nomeEmpresa) return;
                  setDeleting(true);
                  try {
                    await db.deleteEmpresa(deleteModal.empresaId);
                    setDeleteModal(null);
                    setDeleteConfirmText('');
                    await loadData();
                  } catch (e: any) {
                    console.error('Erro ao excluir:', e);
                    setConfirmModal({
                      title: 'Erro ao Excluir',
                      message: `Erro: ${e.message || e}`,
                      onConfirm: () => setConfirmModal(null),
                    });
                  } finally {
                    setDeleting(false);
                  }
                }}
                style={{
                  padding:'8px 16px', borderRadius:7, fontSize:13, fontWeight:500,
                  cursor: deleteConfirmText === deleteModal.nomeEmpresa ? 'pointer' : 'not-allowed',
                  border:'none', color:'#fff',
                  background: deleteConfirmText === deleteModal.nomeEmpresa ? 'var(--red)' : 'rgba(248,81,73,.3)',
                  display:'flex', alignItems:'center', gap:6,
                  transition:'background .15s',
                }}
              >
                {deleting ? (
                  <>Excluindo...</>
                ) : (
                  <>
                    <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <polyline points="3 6 5 6 21 6"/>
                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
                    </svg>
                    Excluir permanentemente
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      {confirmModal && (
        <ConfirmModal
          open={!!confirmModal}
          title={confirmModal.title}
          message={confirmModal.message}
          confirmLabel={confirmModal.confirmLabel}
          cancelLabel={confirmModal.cancelLabel}
          confirmStyle={confirmModal.confirmStyle}
          onConfirm={confirmModal.onConfirm}
          onCancel={confirmModal.onCancel}
        />
      )}
    </>
  );
};
